/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { TickFormatter } from "@visx/axis";
import { NumberValue } from "d3-scale";
import { Box, Col, FlexContainer, Row, Text, theme } from "@nordcloud/gnui";
import {
  ConsumedResource,
  KubernetesType,
  StorageReadWriteResource,
  useResourceConsumptionQuery,
} from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { Legend } from "~/components/Charts/components";
import { isNotEmpty } from "~/tools";
import {
  CHART_COLORS,
  ConsumptionChart,
  ConsumptionChartProps,
} from "./components/ConsumptionChart";
import { byteSize, isNodeInstance, isPodInstance } from "./utils";

const chartLegends = {
  cpuMemory: [
    {
      color: CHART_COLORS.requested,
      opacity: 0.6,
      label: "Avg. Allocated",
    },
    {
      color: CHART_COLORS.used,
      opacity: 0.6,
      label: "Avg. Used",
    },
  ],
  storage: [
    {
      color: CHART_COLORS.used,
      opacity: 0.6,
      label: "Read",
    },
    {
      color: CHART_COLORS.requested,
      opacity: 0.6,
      label: "Write",
    },
  ],
};

const cpuMemoryLegend = (isNode?: boolean) => (
  <Legend
    inline
    data={isNode ? chartLegends.cpuMemory.slice(1) : chartLegends.cpuMemory}
  />
);

const storageLegend = () => <Legend inline data={chartLegends.storage} />;

type ChartProps = {
  caption: string;
  data: ConsumedResource[] | StorageReadWriteResource[];
  legend?: (isNode?: boolean) => JSX.Element;
  axisFormatter?: TickFormatter<NumberValue>;
  isNode?: boolean;
};

function Chart(props: ChartProps) {
  const { caption, data, legend, axisFormatter, isNode } = props;
  const chartProps: ConsumptionChartProps = {
    width: 400,
    height: 120,
    data,
    isNode,
    ...(axisFormatter && { axisFormatter }),
  };
  return (
    <>
      <FlexContainer justifyContent="space-between">
        <Text size="sm" tag="div" weight="medium">
          {caption}
        </Text>
      </FlexContainer>
      <ConsumptionChart {...chartProps} />
      {isNotEmpty(data) && legend && legend(isNode)}
    </>
  );
}

type UtilizationProps = {
  nordcloudId: string;
  k8sResourceType: string;
};

export function Utilization({
  nordcloudId,
  k8sResourceType,
}: UtilizationProps) {
  const { data, loading } = useResourceConsumptionQuery({
    variables: {
      id: nordcloudId,
      type: isPodInstance(k8sResourceType)
        ? KubernetesType.Pod
        : KubernetesType.Node,
    },
  });

  return (
    <Row>
      <Col>
        <Box spacing="spacing04" css={{ overflowX: "scroll" }}>
          <LoaderWrap inContent loading={loading}>
            <Box
              boxStyle="grey"
              px={theme.spacing.spacing03}
              py={theme.spacing.spacing02}
              spacing="spacing04"
            >
              <FlexContainer justifyContent="space-between">
                <Text size="sm" tag="div" weight="medium">
                  Utilization Information
                </Text>
              </FlexContainer>
            </Box>
            <Row>
              <Col>
                <Chart
                  caption="CPU"
                  data={data?.resourceConsumption?.cpu ?? []}
                  legend={cpuMemoryLegend}
                  axisFormatter={(n) => `${n}m`}
                  isNode={isNodeInstance(k8sResourceType)}
                />
              </Col>
              <Col>
                <Chart
                  caption="Storage"
                  data={data?.resourceConsumption?.storageRw ?? []}
                  legend={storageLegend}
                  axisFormatter={(n) => byteSize(n.valueOf())}
                />
              </Col>
              <Col>
                <Chart
                  caption="Memory"
                  data={data?.resourceConsumption?.memory ?? []}
                  legend={cpuMemoryLegend}
                  axisFormatter={(n) => byteSize(n.valueOf())}
                  isNode={isNodeInstance(k8sResourceType)}
                />
              </Col>
            </Row>
          </LoaderWrap>
        </Box>
      </Col>
    </Row>
  );
}
